import { toEtDateString, etDayUtcRange } from './us-eastern-day';
/**
 * NFL Daily Reveal ("Gridiron Kings") Configuration
 *
 * Single source of truth for the NFL *prop-based* Daily Reveal mode. This config
 * is activated only when a Daily Reveal contest has `sport = 'NFL'` AND its
 * resolved mode is `prop_based` (explicit contest override — see `daily-reveal.ts`).
 *
 * NFL's sport-level `defaultMode` remains `stat_hit` so existing / `sport_default`
 * contests keep legacy TD-hit gameplay. New Gridiron Kings contests must set
 * `daily_reveal_mode = 'prop_based'` explicitly.
 *
 * IMPORTANT: This file describes *rules and defaults only*. It must not contain
 * any Sportsbook API / odds-feed client logic. Prop markets are sourced through
 * the provider architecture (manual / mock / csv_import / sportsbook_feed) and
 * frozen into the `daily_reveal_prop_markets` table. The rest of the game only
 * ever consumes those frozen internal records.
 */

/** Comparison operator used to grade a frozen prop against the official stat. */
export type NFLPropOperator = 'over' | 'over_or_equal' | 'over_or_true';

/** Which set of NFL fixtures a day's cards are drawn from. */
export type NFLGameWindow = 'TNF' | 'SUNDAY' | 'MNF' | 'CUSTOM';

/**
 * When a day's REVEAL closes — the last moment its pack can be opened for the
 * first time. Seeds `daily_reveal_days.reveal_close_at`.
 *
 * IT NO LONGER GOVERNS BOTH DEADLINES. This comment used to read "when a day
 * locks for reveals/picks", because one column (`pick_cutoff_at`) carried both
 * concerns. Since DKPLAT-212 they are independent per-day columns:
 * `reveal_close_at` (this rule) closes the reveal and drives `status`
 * `open -> locked`, while `pick_cutoff_at` — the last moment an EXISTING pick
 * can be edited — is seeded separately by `updatePickCutoffForDay` from the
 * earliest included kickoff less a minute. A day is routinely closed for
 * reveals while still open for pick edits; that is the intended model, not a
 * bug.
 *
 * - `end_of_day` — closes at `closeTimeEt` (default 23:59 ET) on the day's own
 *   ET calendar date, but never later than the earliest included kickoff: a
 *   pack picking on a 13:00 slate must not stay openable until 23:59.
 * - `game_window_kickoff` — closes at the configured game window's kickoff.
 * - `first_relevant_game_kickoff` — closes at the earliest kickoff among the
 *   day's eligible markets.
 */
export type NFLDayCloseRule =
  | 'end_of_day'
  | 'game_window_kickoff'
  | 'first_relevant_game_kickoff';

/** How exceptional outcomes (injury/inactive/void/postponed) are graded. */
export type NFLPropExceptionalOutcomePolicy =
  | 'loss'
  | 'void'
  | 'credit_win'
  | 'admin_review';

/** Days of the NFL Daily Reveal week (Tuesday → Monday). */
export type NFLWeekDay =
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY'
  | 'MONDAY';

/** Configuration for a single day/category within the NFL week. */
export interface NFLDailyRevealDayConfig {
  /** 1-based day number within the contest (1 = Tuesday … 7 = Monday). */
  dayNumber: number;
  dayOfWeek: NFLWeekDay;
  /** Stable machine key, e.g. `ground_king`. */
  categoryKey: string;
  /** Display name, e.g. `Ground King`. */
  categoryName: string;
  /** Internal prop stat type, e.g. `rushing_yards`. */
  propStatType: string;
  /** Grading operator for this category's props. */
  operator: NFLPropOperator;
  /** Human-friendly default threshold label used before markets are frozen. */
  defaultThresholdLabel: string;
  gameWindow: NFLGameWindow;
  /** Reveal window open time in ET (24h `HH:mm`). */
  openTimeEt: string;
  closeRule: NFLDayCloseRule;
  /** Only meaningful when `closeRule === 'end_of_day'`. */
  closeTimeEt?: string;
}

/** Top-level NFL Daily Reveal ruleset. */
export interface NFLDailyRevealConfig {
  sport: 'NFL';
  mode: 'prop_based';
  packSize: number;
  weekStartsOn: NFLWeekDay;
  timezone: string;
  /** Whether users may change their pick until the relevant lock deadline. */
  allowPickChangeBeforeLock: boolean;
  /** Whether operators may hand-author prop markets when no feed is available. */
  fallbackManualPropsEnabled: boolean;
  /** Default policy for players ruled out / games disrupted after lock. */
  defaultInjuryPolicy: NFLPropExceptionalOutcomePolicy | 'admin_controlled';
  days: NFLDailyRevealDayConfig[];
}

export const NFL_DAILY_REVEAL_CONFIG: NFLDailyRevealConfig = {
  sport: 'NFL',
  mode: 'prop_based',
  packSize: 3,
  weekStartsOn: 'TUESDAY',
  timezone: 'America/New_York',
  allowPickChangeBeforeLock: true,
  fallbackManualPropsEnabled: true,
  defaultInjuryPolicy: 'admin_controlled',
  days: [
    {
      dayNumber: 1,
      dayOfWeek: 'TUESDAY',
      categoryKey: 'ground_king',
      categoryName: 'Ground King',
      propStatType: 'rushing_yards',
      operator: 'over_or_equal',
      defaultThresholdLabel: '50+ rushing yards',
      gameWindow: 'TNF',
      openTimeEt: '00:00',
      closeRule: 'end_of_day',
      closeTimeEt: '23:59',
    },
    {
      dayNumber: 2,
      dayOfWeek: 'WEDNESDAY',
      categoryKey: 'passing_king',
      categoryName: 'Passing King',
      propStatType: 'passing_yards',
      operator: 'over_or_equal',
      defaultThresholdLabel: '250+ passing yards',
      gameWindow: 'TNF',
      openTimeEt: '00:00',
      closeRule: 'end_of_day',
      closeTimeEt: '23:59',
    },
    {
      dayNumber: 3,
      dayOfWeek: 'THURSDAY',
      categoryKey: 'touchdown_king',
      categoryName: 'Touchdown King',
      propStatType: 'anytime_touchdown',
      operator: 'over_or_true',
      defaultThresholdLabel: 'Score a TD',
      gameWindow: 'TNF',
      openTimeEt: '00:00',
      closeRule: 'game_window_kickoff',
    },
    {
      dayNumber: 4,
      dayOfWeek: 'FRIDAY',
      categoryKey: 'hands_king',
      categoryName: 'Hands King',
      propStatType: 'receiving_yards',
      operator: 'over_or_equal',
      defaultThresholdLabel: '75+ receiving yards',
      gameWindow: 'SUNDAY',
      openTimeEt: '00:00',
      closeRule: 'end_of_day',
      closeTimeEt: '23:59',
    },
    {
      dayNumber: 5,
      dayOfWeek: 'SATURDAY',
      categoryKey: 'sack_king',
      categoryName: 'Sack King',
      propStatType: 'sacks',
      operator: 'over_or_equal',
      defaultThresholdLabel: '1+ sacks',
      gameWindow: 'SUNDAY',
      openTimeEt: '00:00',
      closeRule: 'end_of_day',
      closeTimeEt: '23:59',
    },
    {
      dayNumber: 6,
      dayOfWeek: 'SUNDAY',
      categoryKey: 'shootout_king',
      categoryName: 'Shootout King',
      propStatType: 'passing_touchdowns',
      operator: 'over_or_equal',
      defaultThresholdLabel: '2+ passing TDs',
      gameWindow: 'SUNDAY',
      openTimeEt: '00:00',
      closeRule: 'first_relevant_game_kickoff',
    },
    {
      dayNumber: 7,
      dayOfWeek: 'MONDAY',
      categoryKey: 'crown_king',
      categoryName: 'Crown King',
      propStatType: 'all_purpose_yards',
      operator: 'over_or_equal',
      defaultThresholdLabel: '50+ all-purpose yards',
      gameWindow: 'MNF',
      openTimeEt: '00:00',
      closeRule: 'game_window_kickoff',
    },
  ],
};

/**
 * Format an instant as `YYYY-MM-DD` in America/New_York (matches DK app game dates).
 *
 * Kept as an NFL-named alias of the shared helper: the arithmetic is not NFL-specific and is
 * now needed by the leaderboards too (DKPLAT-796), so there is one implementation of it.
 */
export function toNflEtDateString(instant: Date): string {
  return toEtDateString(instant);
}

/**
 * Human-readable label for which NFL games a reveal day draws props from.
 */
export function describeNflGameWindow(gameWindow: NFLGameWindow): string {
  switch (gameWindow) {
    case 'TNF':
      return 'Thursday Night Football';
    case 'SUNDAY':
      return 'Sunday slate';
    case 'MNF':
      return 'Monday Night Football';
    default:
      return 'Custom window';
  }
}

/**
 * UTC bounds for an entire calendar day in America/New_York (for API date filters).
 * Handles EDT/EST without extra dependencies.
 */
export function getNflEtDayUtcBounds(dateEt: string): { startsAt: Date; endsAt: Date } {
  const { start, end } = etDayUtcRange(dateEt);
  /*
   * INCLUSIVE end, deliberately kept as it was. The shared range is half-open, but this
   * function's value goes into a sportsbook `gameWindow_range` filter, and moving the end
   * from 03:59:59 to 04:00:00 would start including an event sitting exactly on the boundary.
   * Same instants as before; only the DST arithmetic is now shared.
   */
  return { startsAt: start, endsAt: new Date(end.getTime() - 1000) };
}

/**
 * True when a scheduled kickoff belongs to the target NFL fixture date in ET
 * (primary) or UTC calendar date (fallback for feed payloads).
 */
export function kickoffMatchesFixtureDate(kickoff: Date, fixtureDate: string): boolean {
  return toNflEtDateString(kickoff) === fixtureDate || kickoff.toISOString().slice(0, 10) === fixtureDate;
}

const NFL_GAME_WINDOW_DAY_OFFSET: Record<Exclude<NFLGameWindow, 'CUSTOM'>, number> = {
  TNF: 2,
  SUNDAY: 5,
  MNF: 6,
};

/**
 * Return the Tuesday that starts the NFL week containing `gameDate`.
 * NFL weeks run Tuesday → Monday regardless of the day's calendar role.
 */
export function getNflWeekStartTuesday(gameDate: string): string {
  const d = new Date(`${gameDate}T12:00:00Z`);
  const daysSinceTuesday = (d.getUTCDay() + 5) % 7;
  d.setUTCDate(d.getUTCDate() - daysSinceTuesday);
  return d.toISOString().split('T')[0];
}

/**
 * Map a Daily Reveal day's calendar date + game window to the feed-service
 * date that actually hosts NFL fixtures (TNF Thursday, Sunday slate, MNF Monday).
 */
export function resolveNflFixtureDate(gameDate: string, gameWindow: NFLGameWindow): string {
  if (gameWindow === 'CUSTOM') return gameDate;
  const weekStart = getNflWeekStartTuesday(gameDate);
  const target = new Date(`${weekStart}T12:00:00Z`);
  target.setUTCDate(target.getUTCDate() + NFL_GAME_WINDOW_DAY_OFFSET[gameWindow]);
  return target.toISOString().split('T')[0];
}

const NFL_ET_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Validate an optional admin override for the ET fixture calendar date
 * (`config_json.fixtureDateEt`). Returns null when absent or invalid.
 */
export function parseNflFixtureDateEtOverride(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return NFL_ET_DATE_RE.test(trimmed) ? trimmed : null;
}

/**
 * Effective ET fixture calendar date for a reveal day.
 * When `config_json.fixtureDateEt` is set, it wins (holiday / playoff edge cases).
 * Otherwise falls back to the game-window default (`TNF` → Thursday, etc.).
 */
export function resolveEffectiveNflFixtureDate(
  gameDate: string,
  gameWindow: NFLGameWindow,
  configJson?: { fixtureDateEt?: unknown } | null
): string {
  const override = parseNflFixtureDateEtOverride(configJson?.fixtureDateEt);
  if (override) return override;
  return resolveNflFixtureDate(gameDate, gameWindow);
}

/** Look up a day's category config by 1-based day number. */
export function getNflDayConfigByNumber(dayNumber: number): NFLDailyRevealDayConfig | null {
  return NFL_DAILY_REVEAL_CONFIG.days.find(d => d.dayNumber === dayNumber) ?? null;
}

/**
 * Grade a prop result against a frozen threshold + operator.
 * Returns `'won'` or `'lost'` — exceptional outcomes (void/credit) are handled
 * by the scoring worker's policy layer, not here.
 */
export function gradeNflProp(
  resultValue: number,
  threshold: number,
  operator: NFLPropOperator
): 'won' | 'lost' {
  switch (operator) {
    case 'over':
      return resultValue > threshold ? 'won' : 'lost';
    case 'over_or_equal':
      return resultValue >= threshold ? 'won' : 'lost';
    case 'over_or_true':
      return resultValue >= 1 ? 'won' : 'lost';
    default:
      return 'lost';
  }
}
